import type { ReactNode } from 'react';
import { Ban, Check, type LucideIcon } from 'lucide-react';
import { VALUE_PHILOSOPHY_OPTIONS, type BrandTextChange, type ValuePhilosophy } from '@mg/shared';
import { cn } from '../../../lib/cn';
import { ADDED_MARK, REMOVED_MARK } from '../../composer/ThreadPostChanges';
import { PriceTier } from '../../brand/fields/PriceTier';
import type { TagTone } from '../../brand/fields/TagListInput';
import { listDiff, spellsText } from './revisitChanges';
import { REVISIT_COPY } from './revisitCopy';

/**
 * Show changes for one wizard field (spec:134, :139), drawn in place of its control while the switch is on:
 * - text: the field's text, read-only, with the words the re-read added marked green and underlined and
 *   the words it removed struck through in red, word by word against the saved text;
 * - tag lists: every tag, the added ones green and underlined, the removed ones struck through;
 * - Writing guidelines: the rules as rows, marked the same way;
 * - Value philosophy: the old option struck through and the new one marked.
 * Each change carries a visually hidden "Added:" or "Removed:" before it, and removals are struck through,
 * so no change is shown by colour alone. The marks are the composer's Show changes marks (ThreadPostChanges),
 * so both read alike. The marks are never part of the value: what is saved is the field's value alone.
 */

const SR_ONLY = 'sr-only';

/** The box a text field's control draws (components/ui/Field.tsx), greyed as read-only. */
const TEXT_BOX =
  'focus-ring block w-full whitespace-pre-wrap break-words rounded-lg border border-line-input bg-page px-3 py-2 text-sm text-ink';

/** A Textarea of four rows, and a one-line Input, so the field keeps its height (the step does not jump). */
const TEXTAREA_HEIGHT = 'min-h-[6.125rem]';
const INPUT_HEIGHT = 'min-h-[2.375rem]';

/**
 * A character of a script written without spaces between its words - Han, Hiragana, Katakana, Thai, Lao,
 * Khmer, Myanmar - or of the punctuation and full-width forms written with them, as the composer's Show
 * changes and the server's word-align (SPACELESS) know them.
 */
const SPACELESS =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}\p{Script=Lao}\p{Script=Khmer}\p{Script=Myanmar}\u3001-\u303F\u30A0-\u30FF\uFF01-\uFF9F\uFFE0-\uFFE6]/u;
const STARTS_SPACELESS = new RegExp(`^${SPACELESS.source}`, 'u');
const ENDS_SPACELESS = new RegExp(`${SPACELESS.source}$`, 'u');

function Added({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <ins title={REVISIT_COPY.addedTitle} className={cn(ADDED_MARK, className)}>
      <span className={SR_ONLY}>{REVISIT_COPY.addedPrefix}</span>
      {children}
    </ins>
  );
}

function Removed({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <del title={REVISIT_COPY.removedTitle} className={cn(REMOVED_MARK, className)}>
      <span className={SR_ONLY}>{REVISIT_COPY.removedPrefix}</span>
      {children}
    </del>
  );
}

/**
 * A text's parts, marked: its own whitespace is never marked (a line break the AI kept or joined is not a
 * change of words), and removed words stand apart from their neighbours by one space, none beside a
 * script written without spaces. The spaces sit inside the <del>, so the text read without it is the
 * field's text exactly. The same drawing as the composer's (ThreadPostChanges.tsx `marked`).
 */
function marked(changes: readonly BrandTextChange[]): ReactNode[] {
  const nodes: ReactNode[] = [];
  let drawn = '';
  changes.forEach((change, i) => {
    if (change.kind === 'same') {
      nodes.push(change.text);
      drawn += change.text;
      return;
    }
    if (change.kind === 'added') {
      change.text.split(/(\s*\n\s*)/).forEach((piece, j) => {
        if (j % 2 === 1) {
          nodes.push(piece);
          return;
        }
        const [, lead, words, trail] = /^(\s*)([\s\S]*?)(\s*)$/.exec(piece) ?? ['', '', piece, ''];
        if (lead) nodes.push(lead);
        if (words) nodes.push(<Added key={`${i}.${j}`}>{words}</Added>);
        if (trail) nodes.push(trail);
      });
      drawn += change.text;
      return;
    }
    const words = change.text.trim();
    if (!words) return;
    const next = changes.slice(i + 1).find((c) => (c.kind === 'removed' ? c.text.trim() !== '' : c.text !== ''));
    const following = next && (next.kind === 'removed' ? next.text.trim() : next.text);
    const before = drawn !== '' && !/\s$/.test(drawn) && !ENDS_SPACELESS.test(drawn);
    const after = !!following && !/^\s/.test(following) && !STARTS_SPACELESS.test(following);
    nodes.push(
      <del key={i} title={REVISIT_COPY.removedTitle} className="no-underline">
        {before && ' '}
        <span className={REMOVED_MARK}>
          <span className={SR_ONLY}>{REVISIT_COPY.removedPrefix}</span>
          {words}
        </span>
        {after && ' '}
      </del>,
    );
    drawn += `${before ? ' ' : ''}${words}${after ? ' ' : ''}`;
  });
  return nodes;
}

/**
 * A text field's Show changes: its text, read-only, in a box like its control's, named like the control
 * (the field's label) and in the tab order, so it can be read from the keyboard. The marks show only while
 * `changes` still spell the text; otherwise the text shows plain.
 */
export function TextChanges({
  id,
  label,
  text,
  changes,
  multiline,
  describedBy,
}: {
  id: string;
  label: string;
  text: string;
  changes: readonly BrandTextChange[] | null;
  multiline: boolean;
  describedBy?: string;
}) {
  return (
    <div
      id={id}
      role="textbox"
      aria-readonly
      aria-multiline={multiline}
      aria-label={label}
      aria-describedby={describedBy}
      tabIndex={0}
      className={cn(TEXT_BOX, multiline ? TEXTAREA_HEIGHT : INPUT_HEIGHT)}
    >
      {changes != null && spellsText(changes, text) ? marked(changes) : text}
    </div>
  );
}

/** A Vocabulary list's colour (spec:263), as TagListInput draws its tags. */
const TONE_CLASS: Record<TagTone, string> = {
  teal: 'bg-teal-tint text-teal-fg',
  always: 'bg-green-50 text-green-800 dark:bg-green-950/60 dark:text-green-300',
  never: 'bg-red-50 text-red-700 dark:bg-error-dark-tint dark:text-error-dark',
};
const TONE_ICON: Partial<Record<TagTone, LucideIcon>> = { always: Check, never: Ban };
const CHIP = 'inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium';

/**
 * A tag list's Show changes (spec:134): the tags as the list has them now, each kept tag in its list's
 * colour, an added one green and underlined, a removed one struck through in red, in reading order.
 */
export function TagChanges({
  id,
  label,
  saved,
  current,
  tone,
  describedBy,
}: {
  id: string;
  label: string;
  saved: readonly string[];
  current: readonly string[];
  tone: TagTone;
  describedBy?: string;
}) {
  const Icon = TONE_ICON[tone];
  return (
    <ul id={id} aria-label={label} aria-describedby={describedBy} className="flex flex-wrap gap-2">
      {listDiff(saved, current).map((change, index) => (
        // By position, kind and text: a stored list may hold the same text twice (E4).
        <li key={`${index}:${change.kind}:${change.item}`} className="max-w-full">
          {change.kind === 'same' ? (
            <span className={cn(CHIP, TONE_CLASS[tone])}>
              {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden />}
              <span className="min-w-0 break-words">{change.item}</span>
            </span>
          ) : change.kind === 'added' ? (
            <Added className={CHIP}>
              <span className="min-w-0 break-words">{change.item}</span>
            </Added>
          ) : (
            <Removed className={CHIP}>
              <span className="min-w-0 break-words">{change.item}</span>
            </Removed>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Writing guidelines' Show changes: the one-column table of rules (GuidelinesTable), each row marked. */
export function GuidelineChanges({
  id,
  label,
  saved,
  current,
  describedBy,
}: {
  id: string;
  label: string;
  saved: readonly string[];
  current: readonly string[];
  describedBy?: string;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-line">
      <table id={id} aria-label={label} aria-describedby={describedBy} className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th scope="col" className="border-b border-line bg-page px-3 py-2 text-left text-xs font-medium text-ink-muted">
              Guideline
            </th>
          </tr>
        </thead>
        <tbody>
          {listDiff(saved, current).map((change, index) => (
            <tr key={`${index}:${change.kind}:${change.item}`} className="border-b border-line-soft last:border-b-0">
              <td className="whitespace-pre-wrap break-words px-3 py-2 align-top text-ink">
                {change.kind === 'same' ? (
                  change.item
                ) : change.kind === 'added' ? (
                  <Added>{change.item}</Added>
                ) : (
                  <Removed>{change.item}</Removed>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const optionOf = (value: ValuePhilosophy | null) => VALUE_PHILOSOPHY_OPTIONS.find((option) => option.value === value);

/** Value philosophy's Show changes (spec:134): the old option struck through, then the new one marked. */
export function ChoiceChanges({
  id,
  label,
  saved,
  current,
  describedBy,
}: {
  id: string;
  label: string;
  saved: ValuePhilosophy | null;
  current: ValuePhilosophy | null;
  describedBy?: string;
}) {
  const before = optionOf(saved);
  const after = optionOf(current);
  return (
    <div id={id} role="group" aria-label={label} aria-describedby={describedBy} className="space-y-2">
      {before && (
        <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <PriceTier tier={before.tier} />
          <Removed className="rounded px-1">{before.label}</Removed>
          <span className="text-ink-muted">{before.description}</span>
        </p>
      )}
      {after && (
        <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <PriceTier tier={after.tier} />
          <Added className="rounded px-1">{after.label}</Added>
          <span className="text-ink-muted">{after.description}</span>
        </p>
      )}
    </div>
  );
}
